import type {
  CoverageSummary,
  HexSummary,
  NodeDetail,
  NodeQueryResult,
  Stats,
  Summary,
} from '@mcoverage/shared';

export interface CoverageSample {
  lat: number;
  lon: number;
  snr: number;
  fromNode?: number;
  time?: number;
}

export interface FiltersState {
  seenWithinHours: number;
  onlineOnly: boolean;
  includeHidden: boolean;
  batteryMin: number;
  search: string;
  hardware: number[];
  roles: number[];
  regions: string[];
}

export const DEFAULT_FILTERS: FiltersState = {
  seenWithinHours: 30 * 24,
  onlineOnly: false,
  includeHidden: false,
  batteryMin: 0,
  search: '',
  hardware: [],
  roles: [],
  regions: [],
};

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(path, { signal });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText} for ${path}`);
  }
  return (await res.json()) as T;
}

async function post<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok && res.status !== 422) {
    throw new Error(`${res.status} ${res.statusText} for ${path}`);
  }
  // 422 means "nothing valid in that batch" -- a normal outcome worth reporting.
  return (await res.json()) as T;
}

/** Leaflet gives us [south, west, north, east]; the API expects that order too. */
function bboxParam(bounds: { getSouth(): number; getWest(): number; getNorth(): number; getEast(): number }): string {
  return [
    bounds.getSouth().toFixed(4),
    bounds.getWest().toFixed(4),
    bounds.getNorth().toFixed(4),
    bounds.getEast().toFixed(4),
  ].join(',');
}

export function buildQuery(filters: FiltersState, bbox?: string, extra?: Record<string, string>): string {
  const p = new URLSearchParams();
  if (bbox) p.set('bbox', bbox);
  if (filters.seenWithinHours > 0) p.set('seenWithinHours', String(filters.seenWithinHours));
  if (filters.onlineOnly) p.set('onlineOnly', 'true');
  if (filters.includeHidden) p.set('includeHidden', 'true');
  if (filters.batteryMin > 0) p.set('batteryMin', String(filters.batteryMin));
  if (filters.search.trim()) p.set('search', filters.search.trim());
  if (filters.hardware.length) p.set('hardware', filters.hardware.join(','));
  if (filters.roles.length) p.set('roles', filters.roles.join(','));
  if (filters.regions.length) p.set('regions', filters.regions.join(','));
  for (const [k, v] of Object.entries(extra ?? {})) p.set(k, v);
  return p.toString();
}

export const api = {
  stats: (signal?: AbortSignal) => get<Stats>('/api/stats', signal),
  nodes: (filters: FiltersState, bbox?: string, signal?: AbortSignal) =>
    get<NodeQueryResult>(`/api/nodes?${buildQuery(filters, bbox)}`, signal),
  node: (nodeNum: number, signal?: AbortSignal) => get<NodeDetail>(`/api/nodes/${nodeNum}`, signal),
  summary: (filters: FiltersState, bbox?: string, signal?: AbortSignal) =>
    get<Summary>(`/api/summary?${buildQuery(filters, bbox)}`, signal),
  coverage: (resolution: number, bbox?: string, signal?: AbortSignal) => {
    const q = new URLSearchParams({ resolution: String(resolution) });
    if (bbox) q.set('bbox', bbox);
    return get<CoverageSummary>(`/api/coverage?${q.toString()}`, signal);
  },
  submitCoverage: (samples: CoverageSample[], deviceId: string, signal?: AbortSignal) =>
    post<{ stored: number; rejected: number }>('/api/coverage', { samples, deviceId }, signal),
  hexes: (resolution: number, bbox?: string, signal?: AbortSignal) => {
    const q = new URLSearchParams({ resolution: String(resolution) });
    if (bbox) q.set('bbox', bbox);
    return get<HexSummary>(`/api/hexes?${q.toString()}`, signal);
  },
};

export { bboxParam };